import * as React from 'react';

import { calculateLineItemsTotal } from '@rematter/modules/trade/utils/weight';
import { ContentSection } from '@rematter/ui-web';
import { cn, Unit, weightToPounds } from '@rematter/ui-web/lib';
import { useAtomValue } from 'jotai';
import groupBy from 'lodash/groupBy';

import { DebugPanel } from './DebugPanel';
import { FreightChargeCard } from './FreightChargeCard';
import { LoadLevelDeductionsCard } from './LoadLevelDeductionsCard';
import { ReconciliationItemCard } from './ReconciliationItemCard';

import { WeightUnitSwitch } from '~/modules/trade/_components/_shared/WeightUnitSwitch';
import { $materialsReconciliationForm } from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { DeductionTypeRecord } from '~/modules/trade/types/deductionType';
import {
  enumLoadWeightType,
  LoadRecord,
  WeightUnitType,
} from '~/modules/trade/types/load';
import {
  $defaultUnitSystemUnit,
  $salesDefaultWeightDisplayUnit,
} from '~/shared/state/settings.state';

interface MaterialsReconciliationProps {
  load: LoadRecord;
  deductionTypes: DeductionTypeRecord[];
  dialogMode?: boolean;
  disabled?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  onWeightUnitChange?: (unit: WeightUnitType) => void;
  showWeightUnitSwitch?: boolean;
  className?: string;
}

export const MaterialsReconciliation = ({
  load,
  deductionTypes,
  dialogMode = true,
  disabled = false,
  weightUnit: externalWeightUnit,
  facilityDefaultUnit: externalFacilityDefaultUnit,
  onWeightUnitChange,
  showWeightUnitSwitch = true,
  className,
}: MaterialsReconciliationProps) => {
  const t = useTranslations();
  const formState = useAtomValue($materialsReconciliationForm);

  // Default facility mass unit from general settings
  const defaultFacilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);
  // Default weight display unit from sales settings
  const defaultWeightDisplayUnit = useAtomValue($salesDefaultWeightDisplayUnit);

  // Use external props if provided, otherwise use local state
  const [internalWeightUnit, setInternalWeightUnit] =
    React.useState<WeightUnitType>(
      externalWeightUnit || defaultWeightDisplayUnit,
    );

  React.useEffect(() => {
    if (externalWeightUnit) {
      setInternalWeightUnit(externalWeightUnit);
    } else {
      setInternalWeightUnit(defaultWeightDisplayUnit);
    }
  }, [externalWeightUnit, defaultWeightDisplayUnit]);

  const weightUnit =
    externalWeightUnit !== undefined ? externalWeightUnit : internalWeightUnit;
  const facilityDefaultUnit =
    externalFacilityDefaultUnit || defaultFacilityDefaultUnit;

  const handleWeightUnitChange = (unit: WeightUnitType) => {
    if (onWeightUnitChange) {
      onWeightUnitChange(unit);
    } else {
      setInternalWeightUnit(unit);
    }
  };

  const lineItemsByItemId = React.useMemo(() => {
    if (!load?.lineItems) return {};
    return groupBy(load.lineItems, 'itemId');
  }, [load?.lineItems]);

  const hasLineItems = Object.keys(lineItemsByItemId).length > 0;

  const shippedTotal = calculateLineItemsTotal(load.lineItems);

  return (
    <div
      className={cn(
        '@MaterialsReconciliation',
        'min-w-[990px] flex-1',
        className,
      )}
    >
      <ContentSection className="bg-white">
        <div className="flex w-full items-center justify-between px-5 pt-4">
          <h3 className="text-lg font-bold text-neutral">
            {t.materialsReconciliation.materials.title}
          </h3>
          {/* Weight Unit Switch Header - only show if prop is true */}
          {showWeightUnitSwitch && hasLineItems && (
            <WeightUnitSwitch
              value={weightUnit}
              onChange={handleWeightUnitChange}
            />
          )}
        </div>

        <div className="flex-col items-center justify-between px-5">
          {Object.entries(lineItemsByItemId).map(([itemId, items]) => (
            <ReconciliationItemCard
              key={itemId}
              itemId={itemId}
              lineItems={items}
              deductionTypes={deductionTypes}
              dialogMode={dialogMode}
              disabled={disabled}
              weightUnit={weightUnit}
              facilityDefaultUnit={facilityDefaultUnit}
            />
          ))}
          {/* Freight Charge Card */}
          <FreightChargeCard dialogMode={dialogMode} disabled={disabled} />
          {/* Load Level Deductions Card */}
          {load && (
            <LoadLevelDeductionsCard
              deductionTypes={deductionTypes}
              shippedNet={
                load.lineItems?.reduce(
                  (sum, li) =>
                    sum + weightToPounds(li.weights?.[enumLoadWeightType.Net]),
                  0,
                ) || 0
              }
              shippedTotal={shippedTotal}
              disabled={disabled}
            />
          )}
          <DebugPanel hidden formState={formState} />
        </div>
      </ContentSection>
    </div>
  );
};
